/**
 * The Web Storage API instance type. Aliased because this module exports its own
 * `Storage`, and the bare name reads ambiguously next to it.
 */
type WebStorage = typeof globalThis.sessionStorage;

const TEST_KEY = '__jane_storage_test';

/**
 * Reads or writes the storage area, yielding `undefined` when it is unreachable.
 * sessionStorage & localStorage are not available on iframes in incognito sessions.
 * Attempting to use the storage apis will throw an error.
 * @see https://stackoverflow.com/a/16427747
 */
function withStorage<T>(
  getStorage: () => WebStorage,
  operation: (storage: WebStorage) => T
) {
  try {
    return operation(getStorage());
  } catch {
    return undefined;
  }
}

/**
 * Builds a JSON-serializing wrapper around a Web Storage area.
 *
 * NB: the storage area is resolved per call rather than captured here. Bloom renders
 * on the server, where neither `localStorage` nor `sessionStorage` exists, and this
 * module is reachable from SSR code paths.
 */
function createStorage(getStorage: () => WebStorage) {
  return {
    isAvailable() {
      const probe = withStorage(getStorage, (storage) => {
        storage.setItem(TEST_KEY, TEST_KEY);
        storage.removeItem(TEST_KEY);
        return true;
      });

      return probe ?? false;
    },

    set<T>(key: string, data: T) {
      withStorage(getStorage, (storage) =>
        storage.setItem(key, JSON.stringify(data))
      );
    },

    get<T>(key: string): T | undefined {
      const raw = withStorage(getStorage, (storage) => storage.getItem(key));

      if (!raw) return undefined;

      // Keys written before this wrapper existed — or by code that writes the storage
      // area directly — hold raw strings rather than JSON. Fall back to the raw value
      // so those entries stay readable instead of throwing.
      try {
        return JSON.parse(raw);
      } catch {
        return raw as T;
      }
    },

    remove(key: string) {
      withStorage(getStorage, (storage) => storage.removeItem(key));
    },

    clear() {
      withStorage(getStorage, (storage) => storage.clear());
    },
  };
}

export const Storage = createStorage(() => localStorage);

export const SessionStorage = createStorage(() => sessionStorage);

export function isBrowserStorageAvailable() {
  return SessionStorage.isAvailable() && Storage.isAvailable();
}
